import { Form } from "antd";
import { createContext, useContext, useState } from "react";
import type { SearchAudienceResponse } from "../../../shared";
import type {
	AudienceModalContextValue,
	AudienceModalProviderProps,
	AudienceModalSection,
} from "../components/modal/AudienceModalTypes";
import type { AudienceModalStep } from "../types";

const AudienceModalContext = createContext<AudienceModalContextValue | null>(
	null,
);

export const AudienceModalProvider = <
	TSearchForm extends Record<string, any>,
	TCreateForm extends Record<string, any>,
>({
	children,
	visible,
	onClose,
	publicSettings,
}: AudienceModalProviderProps) => {
	const [sections, setSections] = useState<AudienceModalSection[]>([]);
	const [step, setStep] = useState<AudienceModalStep>("search");
	const [searchResults] = useState<SearchAudienceResponse | null>(null);

	const [searchForm] = Form.useForm<TSearchForm>();
	const [createForm] = Form.useForm<TCreateForm>();

	const registerSections = (newSections: AudienceModalSection[]) => {
		setSections(newSections);
	};

	const contextValue: AudienceModalContextValue<TSearchForm, TCreateForm> = {
		// Modal state
		visible,
		onClose,

		// Section management
		sections,
		registerSections,

		// Form instances
		searchForm,
		createForm,

		// Data
		searchResults,
		publicSettings,

		// Step management
		step,
		setStep,
	};

	return (
		<AudienceModalContext.Provider
			value={contextValue as AudienceModalContextValue}
		>
			{children}
		</AudienceModalContext.Provider>
	);
};

export const useAudienceModalContext = <
	TSearchForm extends Record<string, any>,
	TCreateForm extends Record<string, any>,
>(): AudienceModalContextValue<TSearchForm, TCreateForm> => {
	const context = useContext(AudienceModalContext);
	if (!context) {
		throw new Error(
			"useAudienceModalContext must be used within AudienceModalProvider",
		);
	}
	return context as AudienceModalContextValue<TSearchForm, TCreateForm>;
};
